import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { MdClose, MdPerson } from 'react-icons/md';
import { toast } from 'react-toastify';
import {
    createOrganizationStaff,
    updateOrganizationStaff,
    fetchOrganizationRoles
} from '../../../../services/Http/Api';
import MainButton from '../../../../shared/layout/buttons/MainButton';
import { SearchableSelect } from '../../../../shared/layout/forms/AppSelect';

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
`;

const ModalContainer = styled.div`
    background: white;
    border-radius: 12px;
    width: 90%;
    max-width: 500px;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
`;

const ModalHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.25rem 1.5rem;
    border-bottom: 1px solid #e5e7eb;

    h2 {
        margin: 0;
        font-size: 1.25rem;
        font-weight: 600;
        color: #111827;
        display: flex;
        align-items: center;
        gap: 0.75rem;
    }
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    padding: 0.5rem;
    border-radius: 8px;
    color: #6b7280;
    transition: all 0.2s;

    &:hover {
        background: #f3f4f6;
        color: #111827;
    }
`;

const ModalContent = styled.div`
    padding: 1.5rem;
`;

const FormGroup = styled.div`
    margin-bottom: 1.25rem;

    label {
        display: block;
        font-size: 0.875rem;
        font-weight: 500;
        color: #374151;
        margin-bottom: 0.375rem;
    }

    input {
        width: 100%;
        padding: 0.625rem 0.75rem;
        border: 1px solid #d1d5db;
        border-radius: 8px;
        font-size: 0.875rem;
        transition: all 0.2s;

        &:focus {
            outline: none;
            border-color: #821692;
            box-shadow: 0 0 0 3px rgba(130, 22, 146, 0.1);
        }
    }
`;

const FormRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
`;

const ModalFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    padding: 1.25rem 1.5rem;
    border-top: 1px solid #e5e7eb;
    background: #f9fafb;
`;

const CancelButton = styled.button`
    padding: 0.625rem 1.25rem;
    border: 1px solid #d1d5db;
    background: white;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        background: #f3f4f6;
    }
`;

const selectStyles = {
    control: (base, state) => ({
        ...base,
        borderColor: state.isFocused ? '#821692' : '#d1d5db',
        boxShadow: state.isFocused ? '0 0 0 3px rgba(130, 22, 146, 0.1)' : 'none',
        '&:hover': {
            borderColor: '#821692',
        },
    }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected ? '#821692' : state.isFocused ? 'rgba(130, 22, 146, 0.1)' : 'white',
        color: state.isSelected ? 'white' : '#374151',
        cursor: 'pointer',
    }),
    menu: (base) => ({
        ...base,
        zIndex: 9999,
    }),
    menuPortal: (base) => ({
        ...base,
        zIndex: 9999,
    }),
};

const StaffModal = ({ isOpen, onClose, onSave, mode, user }) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        name: '',
        surname: '',
        email: '',
        password: '',
        organization_role_id: '',
    });
    const [roles, setRoles] = useState([]);
    const [saving, setSaving] = useState(false);
    const [loadingRoles, setLoadingRoles] = useState(true);

    useEffect(() => {
        loadRoles();
    }, []);

    useEffect(() => {
        if (mode === 'edit' && user) {
            setFormData({
                name: user.name || '',
                surname: user.surname || '',
                email: user.email || '',
                password: '',
                organization_role_id: user.organization_roles?.[0]?.id || '',
            });
        } else {
            setFormData({
                name: '',
                surname: '',
                email: '',
                password: '',
                organization_role_id: '',
            });
        }
    }, [mode, user]);

    const loadRoles = async () => {
        try {
            const response = await fetchOrganizationRoles();
            setRoles(response.data || []);
        } catch (error) {
            console.error('Error loading roles:', error);
        } finally {
            setLoadingRoles(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleRoleChange = (selectedOption) => {
        setFormData(prev => ({
            ...prev,
            organization_role_id: selectedOption ? selectedOption.value : '',
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.name.trim() || !formData.email.trim()) {
            toast.error(t('OrgUsers.requiredFields'));
            return;
        }

        if (mode === 'create' && !formData.password) {
            toast.error(t('OrgUsers.passwordRequired'));
            return;
        }

        setSaving(true);
        try {
            const payload = {
                name: formData.name,
                surname: formData.surname,
                email: formData.email,
                organization_role_id: formData.organization_role_id || null,
            };

            if (formData.password) {
                payload.password = formData.password;
            }

            if (mode === 'edit') {
                await updateOrganizationStaff(user.id, payload);
                toast.success(t('OrgUsers.updateSuccess'));
            } else {
                await createOrganizationStaff(payload);
                toast.success(t('OrgUsers.createSuccess'));
            }
            onSave();
        } catch (error) {
            console.error('Error saving staff:', error);
            const errorMessage = error.response?.data?.message || error.response?.data?.error;
            toast.error(
                errorMessage ||
                (mode === 'edit'
                    ? t('OrgUsers.updateError')
                    : t('OrgUsers.createError'))
            );
        } finally {
            setSaving(false);
        }
    };

    const roleOptions = roles.map(role => ({
        value: role.id,
        label: role.display_name,
        description: role.description,
    }));

    const selectedRole = roleOptions.find(r => r.value === formData.organization_role_id);

    if (!isOpen) return null;

    return (
        <ModalOverlay onClick={onClose}>
            <ModalContainer onClick={e => e.stopPropagation()}>
                <form onSubmit={handleSubmit}>
                    <ModalHeader>
                        <h2>
                            <MdPerson color="#821692" />
                            {mode === 'edit'
                                ? t('OrgUsers.editStaff')
                                : t('OrgUsers.addStaff')}
                        </h2>
                        <CloseButton type="button" onClick={onClose}>
                            <MdClose size={24} />
                        </CloseButton>
                    </ModalHeader>

                    <ModalContent>
                        <FormRow>
                            <FormGroup>
                                <label>{t('OrgUsers.firstName')} *</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    placeholder={t('OrgUsers.firstNamePlaceholder')}
                                    required
                                />
                            </FormGroup>

                            <FormGroup>
                                <label>{t('OrgUsers.lastName')}</label>
                                <input
                                    type="text"
                                    name="surname"
                                    value={formData.surname}
                                    onChange={handleInputChange}
                                    placeholder={t('OrgUsers.lastNamePlaceholder')}
                                />
                            </FormGroup>
                        </FormRow>

                        <FormGroup>
                            <label>{t('OrgUsers.email')} *</label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                placeholder={t('OrgUsers.emailPlaceholder')}
                                required
                            />
                        </FormGroup>

                        <FormGroup>
                            <label>
                                {t('OrgUsers.password')}
                                {mode === 'create' ? ' *' : ` (${t('OrgUsers.leaveBlank')})`}
                            </label>
                            <input
                                type="password"
                                name="password"
                                value={formData.password}
                                onChange={handleInputChange}
                                placeholder={mode === 'create'
                                    ? t('OrgUsers.passwordPlaceholder')
                                    : t('OrgUsers.passwordPlaceholderEdit')
                                }
                                required={mode === 'create'}
                            />
                        </FormGroup>

                        <FormGroup>
                            <label>{t('OrgUsers.role')}</label>
                            <SearchableSelect
                                options={roleOptions}
                                value={selectedRole}
                                onChange={(_, __, option) => handleRoleChange(option)}
                                placeholder={t('OrgUsers.selectRole')}
                                isClearable
                                isLoading={loadingRoles}
                                styles={selectStyles}
                                menuPortalTarget={document.body}
                                formatOptionLabel={(option) => (
                                    <div>
                                        <div style={{ fontWeight: 500 }}>{option.label}</div>
                                        {option.description && (
                                            <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                                {option.description}
                                            </div>
                                        )}
                                    </div>
                                )}
                            />
                        </FormGroup>
                    </ModalContent>

                    <ModalFooter>
                        <CancelButton type="button" onClick={onClose}>
                            {t('common.cancel')}
                        </CancelButton>
                        <MainButton type="submit" disabled={saving}>
                            {saving
                                ? t('common.saving')
                                : mode === 'edit'
                                    ? t('common.update')
                                    : t('common.create')}
                        </MainButton>
                    </ModalFooter>
                </form>
            </ModalContainer>
        </ModalOverlay>
    );
};

export default StaffModal;
